import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { IconButton } from '../../button/button';
import { DomElementAlignment, DomTargetPosition } from '../../enums';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';
import Tooltip from '../../tooltip-component/tooltip.component';
import { iconMap } from '../icon-mapper';
import { NavigationItemTooltip } from './navigation-item.tooltip';
import type { SideNavigationItem } from './navigation-items.interfaces';

interface NavigationItemInnerElementProps {
  item: SideNavigationItem;
  isItemCollapsed?: boolean;
  isParentItem?: boolean;
  expandedSubItems?: boolean;
  isMobile: boolean;
  handleSubItemsExpanded?: (item: SideNavigationItem) => void;
}

function NavigationItemInnerElement({
  item,
  isItemCollapsed,
  isParentItem,
  expandedSubItems,
  isMobile,
  handleSubItemsExpanded,
}: NavigationItemInnerElementProps) {
  const [, setRenderTrigger] = useState(false);

  const labelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // Trigger re-render to ensure tooltips are calculated correctly after initial render
    if (!item?.badge?.showLoadingIndicator) {
      setRenderTrigger((prev) => !prev);
    }
  }, [item.badge?.showLoadingIndicator]);

  const navItemTextStyles = clsx(
    'odin-flex odin-flex-1 odin-text-sm odin-overflow-hidden odin-transition-opacity odin-duration-300'
  );

  const navItemBadgeStyles = clsx(
    'odin-rounded-lg odin-text-center odin-min-w-min odin-py-1 sm:odin-py-0 odin-px-2 sm:odin-px-1 odin-text-xs sm:odin-text-xxs odin-bg-primary-1 odin-text-mono-1 odin-transition-opacity focus:outline-none dark:odin-text-mono-0',
    {
      'odin-bg-transparent odin-text-primary-1 absolute top-0 right-0 !odin-mr-0': isItemCollapsed,
      'odin-hidden': !isItemCollapsed && !item.badge?.badgeNumber,
      'odin-cursor-default': isMobile,
    }
  );

  const subAngleButtonStyles = clsx('odin-transition-transform odin-duration-100 odin-ml-auto', {
    '-odin-rotate-180': !expandedSubItems,
  });

  const spinnerStyles = clsx({ 'absolute top-0 right-0 !odin-mr-0': isItemCollapsed });

  return (
    <>
      <Tooltip
        description={item.label}
        disabled={isMobile || !isItemCollapsed}
        debounceTime={0}
        exitDebounceTime={0}
        wrapperClassName={clsx('odin-px-2', { 'odin-w-full': isItemCollapsed })}
        tooltipAlignment={DomElementAlignment.TopLeft}
        tooltipTargetPosition={DomTargetPosition.BottomRight}
      >
        <OdinIcon
          className={clsx({ 'odin-place-self-center': isItemCollapsed })}
          icon={iconMap[item.icon] || 'ClipboardList'}
          size={OdinIconSize.ExtraSmall}
        />
      </Tooltip>
      {!isItemCollapsed && (
        <Tooltip
          description={item.label}
          disabled={(labelRef.current?.offsetWidth ?? 0) >= (labelRef.current?.scrollWidth ?? 0)}
          wrapperClassName={navItemTextStyles}
          tooltipAlignment={DomElementAlignment.TopLeft}
          tooltipTargetPosition={DomTargetPosition.BottomRight}
          debounceTime={0}
          exitDebounceTime={0}
        >
          <span ref={labelRef} className="odin-inline-block odin-truncate odin-w-full">
            {item.label}
          </span>
        </Tooltip>
      )}
      {item?.badge?.showLoadingIndicator ? (
        <OdinIcon icon="Loader" spin={true} className={spinnerStyles} size={OdinIconSize.ExtraSmall} />
      ) : (
        item?.badge?.badgeNumber && (
          <NavigationItemTooltip item={item} disabled={isMobile}>
            <button id="badge-clickable" className={navItemBadgeStyles}>
              {item?.badge?.badgeNumber}
            </button>
          </NavigationItemTooltip>
        )
      )}
      {isParentItem && item.collapsedSubItems && (
        <IconButton
          onClick={() => handleSubItemsExpanded?.(item)}
          className={subAngleButtonStyles}
          icon={expandedSubItems ? 'ChevronUp' : 'ChevronDown'}
          iconClassName="odin-bottom-0.3 odin-relative odin-left-3.5"
        />
      )}
    </>
  );
}

export default NavigationItemInnerElement;
